import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldAlert, Loader2, RefreshCw, Search, ChevronRight, ExternalLink, CheckCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

export const fmtWhen = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export const Card = ({ title, hint, right, children, testid }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 sm:p-4 mb-4" data-testid={testid}>
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <h3 className="font-bold text-slate-800 text-sm">{title}</h3>
      {hint && <p className="text-[11px] text-slate-500 w-full sm:w-auto sm:ml-1">{hint}</p>}
      <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-center gap-2">{right}</div>
    </div>
    {children}
  </div>
);

export const Empty = ({ children = 'Nothing to show', testid }) => (
  <p className="text-xs text-slate-400 text-center py-8" data-testid={testid}>{children}</p>
);

export const Spin = () => (
  <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
);

/** Horizontally scrollable table wrapper — keeps the section usable on a phone. */
export const Scroll = ({ children, testid }) => (
  <div className="-mx-3 sm:mx-0 overflow-x-auto" data-testid={testid}>
    <div className="min-w-[640px] px-3 sm:px-0 sm:min-w-0">{children}</div>
  </div>
);

export const TH = ({ children, right }) => (
  <th className={`px-2.5 py-2 text-[10px] uppercase tracking-wide text-slate-500 font-bold ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

/** True below `bp` px. Used to render EITHER the table OR the card list,
 *  never both — duplicated DOM would duplicate every data-testid. */
export function useIsMobile(bp = 768) {
  const [m, setM] = useState(
    typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${bp - 1}px)`);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [bp]);
  return m;
}

/**
 * One table definition, two layouts: a real table on desktop and stacked
 * label/value cards on a phone — so every Security list stays readable
 * without pinch-zoom or sideways scrolling.
 *
 * cols: [{ key, label, render(row, i), primary?, right?, full? }]
 *   primary — shown as the card headline on mobile (e-mail, IP, …)
 *   full    — spans both mobile columns (actions, long text)
 */
export const DataTable = ({ cols, rows, rowKey, rowTestid, testid }) => {
  const mobile = useIsMobile();
  const key = (r, i) => (rowKey ? rowKey(r, i) : i);
  const tid = (r, i) => (rowTestid ? rowTestid(r, i) : undefined);

  if (!mobile) {
    return (
      <div className="overflow-x-auto" data-testid={testid}>
        <table className="w-full text-xs">
          <thead className="bg-slate-100">
            <tr>{cols.map((c) => <TH key={c.key} right={c.right}>{c.label}</TH>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={key(r, i)} className="border-t border-slate-100" data-testid={tid(r, i)}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-2.5 py-2 ${c.right ? 'text-right' : ''} ${c.tdClass || ''}`}>
                    {c.render(r, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const head = cols.filter((c) => c.primary);
  const rest = cols.filter((c) => !c.primary);
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={key(r, i)} data-testid={tid(r, i)}
          className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
          {!!head.length && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2 text-sm font-semibold text-slate-900 break-words">
              {head.map((c) => <div key={c.key} className="min-w-0 break-words">{c.render(r, i)}</div>)}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {rest.map((c) => (
              <div key={c.key} className={`min-w-0 ${c.full ? 'col-span-2' : ''}`}>
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{c.label}</p>
                <div className="text-xs text-slate-700 break-words">{c.render(r, i)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const CustomerLink = ({ email, userId, onOpenCustomer, testid }) => {
  if (!email) return <span className="text-slate-400">—</span>;
  if (!userId || !onOpenCustomer) return <span className="text-slate-700">{email}</span>;
  return (
    <button type="button" data-testid={testid}
      onClick={() => onOpenCustomer(userId)}
      className="text-cyan-700 hover:text-cyan-900 font-medium hover:underline text-left">
      {email}
    </button>
  );
};

export function useApi(token, path, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}${path}`, { headers: authHeaders(token) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setData(j);
    } catch (e) {
      toast.error(e.message || 'Load failed');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, path, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, reload: load };
}

/** Counter tile on the Overview — click jumps to the right tab. */
export const Counter = ({ label, value, hint, tone = 'slate', onClick, testid }) => {
  const tones = {
    slate: 'bg-white border-slate-200 text-slate-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-300 text-rose-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    violet: 'bg-violet-50 border-violet-200 text-violet-800',
  };
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left p-3 rounded-xl border shadow-sm transition ${tones[tone]} ${onClick ? 'hover:-translate-y-0.5 hover:shadow' : ''}`}>
      <p className="text-[10px] font-bold uppercase tracking-wider opacity-70 leading-tight">{label}</p>
      <p className="text-2xl font-extrabold tabular-nums mt-1 leading-none">{value}</p>
      <p className="text-[10px] opacity-70 mt-1 flex items-center gap-0.5">
        {hint}{onClick && <ChevronRight className="w-3 h-3" />}
      </p>
    </button>
  );
};

export { API, Search, RefreshCw, ShieldAlert, ExternalLink, CheckCheck, useMemo };
